import { useEffect, useState, type FormEvent } from 'react';
import { Settings, Save, LineChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePlatformGeneral, useSaveGeneral } from '@/hooks/usePlatform';
import { ApiError } from '@/lib/api-client';

export function GeneralSettingsPage() {
  const { data, isPending } = usePlatformGeneral();
  const save = useSaveGeneral();

  const [platformName, setPlatformName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [tagline, setTagline] = useState('');
  const [gaId, setGaId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.general) {
      setPlatformName(data.general.platform_name ?? '');
      setSupportEmail(data.general.support_email ?? '');
      setTagline(data.general.tagline ?? '');
      setGaId(data.general.google_analytics_id ?? '');
    }
  }, [data]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await save.mutateAsync({
        platform_name: platformName || undefined,
        support_email: supportEmail || undefined,
        tagline: tagline || undefined,
        // Empty string clears the id; undefined would leave it unchanged.
        google_analytics_id: gaId.trim(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  if (isPending) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Settings className="w-5 h-5" /> Genel Ayarlar
        </h1>
        <p className="text-muted-foreground text-sm">Platformun genel kimliği.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Marka</CardTitle>
          <CardDescription>Landing page ve email'lerde kullanılır.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="platform_name">Platform Adı</Label>
              <Input
                id="platform_name"
                value={platformName}
                onChange={(e) => setPlatformName(e.target.value)}
                placeholder="CMS Hub"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline">Slogan</Label>
              <Input
                id="tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="AI ile içerik üret, WordPress'e saniyeler içinde gönder"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support_email">Destek E-postası</Label>
              <Input
                id="support_email"
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="destek@..."
              />
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label htmlFor="ga_id" className="flex items-center gap-1.5">
                <LineChart className="w-3.5 h-3.5" /> Google Analytics 4 — Ölçüm ID
              </Label>
              <Input
                id="ga_id"
                value={gaId}
                onChange={(e) => setGaId(e.target.value)}
                placeholder="G-XXXXXXXXXX"
                pattern="^(G-[A-Za-z0-9]+)?$"
              />
              <p className="text-xs text-muted-foreground">
                Ayarlandığında gtag.js snippet'i otomatik olarak tüm sayfa HTML'ine enjekte edilir.
                Google Analytics panelinde <strong>Yönetici → Mülkler → Veri akışları</strong>'ndan{' '}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">G-…</code> ile başlayan ölçüm ID'yi kopyala.
                Boş bırakırsan takip tamamen devre dışı kalır.
              </p>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                {error}
              </div>
            )}
            {saved && (
              <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
                Kaydedildi ✓
              </div>
            )}
            <Button type="submit" disabled={save.isPending}>
              <Save className="w-4 h-4" /> {save.isPending ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
